import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "antd";
import type { FormInstance } from "antd";
import {
  useGetMasterPackagingType,
  useGetMasterPackagingTypeById,
} from "hooks/useMasterData";
import type { MasterPackagingTypeData } from "services/interface";
import {
  dimensionsFromPackaging,
  isPackagingSelectionIncomplete,
  shouldHideAutoDimensions,
  shouldShowDimensionInputFields,
  shouldShowManualDimensionFields,
} from "../parcelPackagingLogic";

/** Logic เลือกบรรจุภัณฑ์ — อ้างอิง ProductSKUCreate / SKUModalForm */
export function useParcelPackagingSelection(form: FormInstance) {
  const { data: packagingTypeData, isLoading: packagingTypeLoading } =
    useGetMasterPackagingType();

  const parentPackagingTypeId = Form.useWatch("parent_packaging_type_id", form);
  const packagingTypeId = Form.useWatch("packaging_type_id", form);
  const boxWeight = Form.useWatch("box_weight", form);
  const boxWidth = Form.useWatch("box_width", form);
  const boxLength = Form.useWatch("box_length", form);
  const boxHeight = Form.useWatch("box_height", form);

  const parentList = packagingTypeData?.data?.master_packaging_types ?? [];
  const selectedParent = parentList.find((p) => p.id === parentPackagingTypeId);
  const showChildDropdown = !!selectedParent?.has_child;

  const { data: childPackagingTypeData, isLoading: childPackagingTypeLoading } =
    useGetMasterPackagingType(parentPackagingTypeId || undefined);

  const currentPackagingTypeId = packagingTypeId || parentPackagingTypeId;
  const isMissingChild =
    !!currentPackagingTypeId &&
    !parentList.some((p) => p.id === currentPackagingTypeId);

  const { data: missingChildData } = useGetMasterPackagingTypeById(
    isMissingChild ? currentPackagingTypeId : null
  );

  const [resolvedParentId, setResolvedParentId] = useState<string | null>(null);

  useEffect(() => {
    if (parentPackagingTypeId) {
      setResolvedParentId(parentPackagingTypeId);
      return;
    }
    if (isMissingChild && missingChildData?.data?.id) {
      const child = missingChildData.data;
      const parent = parentList.find((p) => p.has_child && child.type === p.type);
      if (parent) {
        setResolvedParentId(parent.id);
      }
    }
  }, [parentPackagingTypeId, isMissingChild, missingChildData, parentList]);

  const effectiveParentId = parentPackagingTypeId || resolvedParentId;

  const selectedPackaging: MasterPackagingTypeData | undefined = useMemo(() => {
    const id = packagingTypeId || (!showChildDropdown ? parentPackagingTypeId : undefined);
    if (!id) return undefined;
    const fromChild = childPackagingTypeData?.data?.master_packaging_types?.find(
      (c) => c.id === id
    );
    if (fromChild) return fromChild;
    return parentList.find((p) => p.id === id) || missingChildData?.data;
  }, [
    packagingTypeId,
    parentPackagingTypeId,
    showChildDropdown,
    childPackagingTypeData,
    parentList,
    missingChildData,
  ]);

  const packagingIncomplete = isPackagingSelectionIncomplete(
    parentPackagingTypeId,
    showChildDropdown,
    packagingTypeId
  );

  const hideAutoDimensions = shouldHideAutoDimensions(
    showChildDropdown,
    selectedPackaging
  );

  const showManualDimensions = shouldShowManualDimensionFields(
    packagingIncomplete,
    showChildDropdown,
    selectedPackaging
  );

  const showDimensionInputs = shouldShowDimensionInputFields(
    packagingIncomplete,
    showManualDimensions,
    hideAutoDimensions
  );

  const prevHideAutoDimensionsRef = useRef(false);

  useEffect(() => {
    if (!selectedPackaging) {
      prevHideAutoDimensionsRef.current = false;
      return;
    }

    if (hideAutoDimensions) {
      form.setFieldsValue(dimensionsFromPackaging(selectedPackaging));
      prevHideAutoDimensionsRef.current = true;
      return;
    }

    if (showManualDimensions && prevHideAutoDimensionsRef.current) {
      form.setFieldsValue({
        box_width: undefined,
        box_length: undefined,
        box_height: undefined,
      });
    }
    prevHideAutoDimensionsRef.current = false;
  }, [selectedPackaging?.id, hideAutoDimensions, showManualDimensions, form]);

  return {
    parentList,
    packagingTypeLoading,
    childPackagingTypeLoading,
    parentPackagingTypeId,
    packagingTypeId,
    boxWeight,
    boxWidth,
    boxLength,
    boxHeight,
    showChildDropdown,
    effectiveParentId,
    childPackagingTypeData,
    selectedPackaging,
    packagingIncomplete,
    hideAutoDimensions,
    showManualDimensions,
    showDimensionInputs,
  };
}
